import React from 'react'
import { useAuth } from './AuthContext'
import { useToast } from './ToastContext'
import { useAnnouncement } from './AnnouncementContext'

const WebSocketContext = React.createContext(null)

const WS_BASE = process.env.REACT_APP_WS_URL || 'ws://localhost:8078'

export function WebSocketProvider({ children }) {
    const { user } = useAuth()
    const { addToast } = useToast()
    const { announce } = useAnnouncement()
    const [lastMessage, setLastMessage] = React.useState(null)
    const wsRef = React.useRef(null)
    const shouldReconnect = React.useRef(true)
    const hasConnected = React.useRef(false)

    React.useEffect(() => {
        if (!user) return

        shouldReconnect.current = true
        hasConnected.current = false

        function connect() {
            if (!shouldReconnect.current) return

            const ws = new WebSocket(`${WS_BASE}/ws/${user.id}`)
            wsRef.current = ws

            ws.onopen = () => {
                if (hasConnected.current) {
                    setLastMessage({ event: 'ws_reconnected' })
                }
                hasConnected.current = true
            }

            ws.onmessage = (event) => {
                try {
                    const payload = JSON.parse(event.data)
                    setLastMessage(payload)

                    if (payload.event === 'game_invitation')
                        addToast(`${payload.from} invited you to a game!`, 'info')
                    else if (payload.event === 'your_turn')
                        addToast(`Your turn — Round ${payload.round}`, 'success')
                    else if (payload.event === 'game_started')
                        addToast('Game has started!', 'success')
                    else if (payload.event === 'player_joined')
                        addToast(`A player joined your game (${payload.player_count} players)`, 'info')
                    else if (payload.event === 'game_over')
                        addToast('Game over!', 'info')
                    else if (payload.event === 'announcement')
                        announce(payload.message)
                } catch {
                    // ignore malformed messages
                }
            }

            ws.onclose = () => {
                if (shouldReconnect.current) {
                    setTimeout(connect, 3000)
                }
            }
        }

        connect()

        return () => {
            shouldReconnect.current = false
            wsRef.current?.close()
        }
    }, [user?.id]) // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <WebSocketContext.Provider value={{ lastMessage }}>
            {children}
        </WebSocketContext.Provider>
    )
}

export function useWebSocket() {
    return React.useContext(WebSocketContext)
}
